import { Box, ClickAwayListener, Popper, type PopperProps } from '@mui/material';
import { offset as floatingOffset, shift } from '@floating-ui/react';
import { PopoverPrimitive, type PopoverPrimitiveOptions } from '@figma/fpl-components';
import { ColorTheme } from '@figma/fpl-themes';
import fplTokens from '@figma/fpl-tokens/css-vars';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { useThemeMode } from '@/contexts/ThemeModeContext';
import { color } from '@/colors';
import { ZIndex } from '@/consts/z-indexes.consts';
import { getFplDataAttributes } from '@/UI/Buttons/fplDataAttributes';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';

export interface FloatingPanelProps extends Omit<PopperProps, 'children'> {
  children: React.ReactNode;
  fplPopoverType?: PopoverPrimitiveOptions['type'];
  onClose?: () => void;
  /** Reports the resolved placement after collision handling. */
  onPlacementChange?: (placement: NonNullable<PopperProps['placement']>) => void;
  triggerRef?: React.RefObject<HTMLElement | null>;
  offset?: number;
  skidding?: number;
  /** Draws the FPL popover tail pointing at the anchor. Flag-on only; the legacy Popper has no tail. */
  showArrow?: boolean;
  /** Keeps the tail clear of the panel's rounded corners. */
  arrowPadding?: number;
  /**
   * Stay on `placement`. Shift along that side to stay on screen instead of flipping onto the
   * anchor, which is what a tall coachmark beside a bottom-of-panel section would do.
   */
  stayOnSide?: boolean;
}

// Matches the tail the FPL Tooltip draws (see `UI/Tooltip`): 12px base, 6px protrusion.
const ARROW_WIDTH = 12;
const ARROW_HEIGHT = 6;

const FLOATING_PANEL_CHROME = {
  alignItems: 'flex-start',
  background: token.bg,
  border: `1px solid ${color.White08_T}`,
  borderRadius: '8px',
  display: 'inline-flex',
  flexDirection: 'column',
  gap: 0.75,
  padding: 2,
  zIndex: 1,
} as const;

// Flag-on surface mirrors the FPL popover/dialog surface (see fpl `dialog-common`
// `customContents`): token background/text, `radius.large`, and `elevation.x500`. Like FPL, the
// hairline border is simulated by the `inset` shadow segments baked into `elevation.x500` — there
// is no separate CSS border. These tokens resolve via the surrounding ColorTheme in the portal.
const FPL_FLOATING_PANEL_CHROME = {
  alignItems: 'flex-start',
  background: fplTokens.color.bg,
  borderRadius: fplTokens.radius.large,
  boxShadow: fplTokens.elevation.x500,
  color: fplTokens.color.text,
  display: 'inline-flex',
  flexDirection: 'column',
  gap: fplTokens.spacer.x1,
  padding: fplTokens.spacer.x16px,
  zIndex: 1,
} as const;

const LegacyFloatingPanel = ({
  children,
  onClose,
  onPlacementChange,
  open,
  anchorEl,
  triggerRef,
  fplPopoverType: _fplPopoverType,
  offset = 10,
  skidding = 0,
  showArrow: _showArrow,
  arrowPadding: _arrowPadding,
  stayOnSide = false,
  sx,
  ...props
}: FloatingPanelProps) => {
  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!open) return;
      if (anchorEl && 'contains' in anchorEl && anchorEl.contains(event.target as Node)) return;
      if (triggerRef && triggerRef.current && triggerRef.current.contains(event.target as Node)) return;
      onClose?.();
    },
    [onClose, open, anchorEl, triggerRef],
  );

  const [isPositioned, setIsPositioned] = useState(false);

  const popperOptions = useMemo(
    () => ({
      strategy: 'fixed' as const,
    }),
    [],
  );

  const modifiers = useMemo<NonNullable<PopperProps['modifiers']>>(
    () => [
      { name: 'offset', options: { offset: [skidding, offset] } },
      // Default Popper flips to the opposite side when the panel doesn't fit. A coachmark beside
      // the Tool panel should slide along that side instead.
      { name: 'flip', enabled: !stayOnSide },
      {
        name: 'eventListeners',
        enabled: true,
      },
      {
        name: 'updateState',
        enabled: true,
        phase: 'write',
        fn: ({ state }) => {
          onPlacementChange?.(state.placement);
          // After the position update, mark as positioned
          setIsPositioned(true);
        },
        requires: ['computeStyles'],
      },
    ],
    [offset, skidding, stayOnSide, onPlacementChange],
  );

  useEffect(() => {
    if (!open) {
      setIsPositioned(false);
    }
  }, [open]);

  return (
    <ClickAwayListener mouseEvent="onMouseDown" touchEvent="onTouchStart" onClickAway={handleClickAway}>
      <Popper
        open={Boolean(open && anchorEl)}
        anchorEl={anchorEl}
        popperOptions={popperOptions}
        modifiers={modifiers}
        sx={{
          zIndex: ZIndex.Overlays.ANCHORED_POPPER_ABOVE_DIALOG,
          visibility: isPositioned ? 'visible' : 'hidden',
        }}
        {...props}
      >
        <AnimatePresence>
          {open && anchorEl ? (
            <Box
              key="floating-panel"
              component={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: isPositioned ? 1 : 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              sx={{
                ...FLOATING_PANEL_CHROME,
                ...(sx || {}),
              }}
            >
              {children}
            </Box>
          ) : null}
        </AnimatePresence>
      </Popper>
    </ClickAwayListener>
  );
};

const FplFloatingPanel = ({
  children,
  onClose,
  onPlacementChange,
  open,
  anchorEl,
  triggerRef,
  fplPopoverType = 'menu',
  offset = 10,
  skidding = 0,
  showArrow = false,
  arrowPadding = 0,
  stayOnSide = false,
  sx,
  ...props
}: FloatingPanelProps) => {
  const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);
  // The popover container renders in a portal, outside the app's ColorTheme.
  const themeMode = useThemeMode();

  const resolvedAnchor = (typeof anchorEl === 'function' ? anchorEl() : anchorEl) ?? null;

  const isOpen = Boolean(open && resolvedAnchor);

  const placement = useMemo<NonNullable<PopoverPrimitiveOptions['placement']>>(() => {
    const requested = props.placement;
    // MUI's `auto*` placements have no Floating UI equivalent; the legacy MUI default was `bottom`.
    if (!requested || requested.startsWith('auto')) return 'bottom';
    return requested as NonNullable<PopoverPrimitiveOptions['placement']>;
  }, [props.placement]);

  // Passing middleware replaces the primitive's offset + flip chain, so offset is repeated here.
  // Shift keeps a bottom-anchored coachmark on the left of the panel instead of flipping above it.
  const stayOnSideMiddleware = useMemo(
    () => (stayOnSide ? [floatingOffset({ mainAxis: offset, crossAxis: skidding }), shift({ padding: 8 })] : undefined),
    [offset, skidding, stayOnSide],
  );

  const { getArrowProps, getContainerProps, context } = PopoverPrimitive.usePopover({
    arrowPadding,
    isOpen,
    allowScrimPassthrough: fplPopoverType === 'tutorial', //Recalculate the tooltip position every animation frame.
    onOpenChange: (nextOpen) => {
      if (!nextOpen) onClose?.();
    },
    // `menu` remains the general default; product tours use `tutorial` so they stack above
    // dropdown backdrops without taking on menu dismissal behavior.
    type: fplPopoverType,
    softDismiss: false,
    provideOwnDismiss: true,
    provideOwnClick: true,
    placement,
    offset: { mainAxis: offset, crossAxis: skidding },
    middleware: stayOnSideMiddleware,
    // Preserve the legacy `strategy: 'fixed'` so anchors inside scrolled/transformed
    // containers stay pinned. `strategy` is forwarded to Floating UI's `useFloating`.
    strategy: 'fixed',
  } as PopoverPrimitiveOptions);

  useLayoutEffect(() => {
    context.refs.setPositionReference(resolvedAnchor);
  }, [context.refs, resolvedAnchor]);

  useLayoutEffect(() => {
    if (isOpen && context.isPositioned) {
      onPlacementChange?.(context.placement);
    }
  }, [isOpen, context.isPositioned, context.placement, onPlacementChange]);

  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!isOpen) return;
      const target = event.target as Node | null;
      if (!target) return;
      if (containerEl && containerEl.contains(target)) return;
      // Nested FPL selects own their portaled options through aria-controls.
      if (containerEl) {
        const triggers = containerEl.querySelectorAll('[aria-controls]');
        for (const trigger of triggers) {
          const controlledIds = trigger.getAttribute('aria-controls')?.split(/\s+/) ?? [];
          if (controlledIds.some((id) => document.getElementById(id)?.contains(target))) return;
        }
      }
      if (resolvedAnchor && 'contains' in resolvedAnchor && (resolvedAnchor as Element).contains(target)) return;
      if (triggerRef && triggerRef.current && triggerRef.current.contains(target)) return;
      onClose?.();
    },
    [isOpen, containerEl, resolvedAnchor, triggerRef, onClose],
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('mousedown', handleClickAway, true);
    document.addEventListener('touchstart', handleClickAway, true);
    return () => {
      document.removeEventListener('mousedown', handleClickAway, true);
      document.removeEventListener('touchstart', handleClickAway, true);
    };
  }, [isOpen, handleClickAway]);

  const dataAttributes = getFplDataAttributes(props);

  const sanitizedSx = useMemo(() => {
    if (!sx) return undefined;
    const next = { ...(sx as Record<string, unknown>) };
    delete next.background;
    delete next.backgroundColor;
    delete next.border;
    return next;
  }, [sx]);

  return (
    <PopoverPrimitive.Container {...getContainerProps({ ref: setContainerEl })} {...dataAttributes}>
      <ColorTheme brand="weave" mode={themeMode}>
        {showArrow && (
          <PopoverPrimitive.Arrow
            {...getArrowProps()}
            data-testid="floating-panel-arrow"
            fill={fplTokens.color.bg}
            height={ARROW_HEIGHT}
            width={ARROW_WIDTH}
          />
        )}
        <Box
          sx={{
            ...FPL_FLOATING_PANEL_CHROME,
            ...(sanitizedSx || {}),
          }}
        >
          {children}
        </Box>
      </ColorTheme>
    </PopoverPrimitive.Container>
  );
};

export const FloatingPanel = (props: FloatingPanelProps) => {
  const isFplMigrationEnabled = useFplMigrationFlag();

  return isFplMigrationEnabled ? <FplFloatingPanel {...props} /> : <LegacyFloatingPanel {...props} />;
};
